"use client";

import {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useMemo,
    type HTMLAttributes,
    type ComponentProps,
    type KeyboardEvent,
} from "react";
import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import ArrowRightIcon from "@/icons/arrows/arrow-right-bold.svg";

const DESKTOP_MEDIA_QUERY = "(min-width: 1024px)";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

export type CarouselProps = {
    opts?: CarouselOptions;
    plugins?: CarouselPlugin;
    orientation?: "horizontal" | "vertical";
    setApi?: (api: CarouselApi) => void;
    loop?: boolean;
    desktopWheelGestures?: boolean;
};

type CarouselContextProps = {
    carouselRef: ReturnType<typeof useEmblaCarousel>[0];
    api: ReturnType<typeof useEmblaCarousel>[1];
    scrollPrev: () => void;
    scrollNext: () => void;
    canScrollPrev: boolean;
    canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = createContext<CarouselContextProps | null>(null);

function useCarousel() {
    const context = useContext(CarouselContext);

    if (!context) {
        throw new Error("useCarousel must be used within a <Carousel />");
    }

    return context;
}

const Carousel = ({
    orientation = "horizontal",
    opts,
    setApi,
    plugins,
    className,
    children,
    loop = true,
    desktopWheelGestures = false,
    ...props
}: HTMLAttributes<HTMLDivElement> & CarouselProps) => {
    const [wheelGesturesPlugin] = useState(() =>
        WheelGesturesPlugin({
            active: false,
            breakpoints: {
                [DESKTOP_MEDIA_QUERY]: {
                    active: true,
                },
            },
        })
    );
    const carouselPlugins = useMemo(
        () => desktopWheelGestures
            ? [...(plugins ?? []), wheelGesturesPlugin]
            : plugins ?? [],
        [desktopWheelGestures, plugins, wheelGesturesPlugin]
    );
    const [carouselRef, api] = useEmblaCarousel(
        {
            ...opts,
            loop,
            axis: orientation === "horizontal" ? "x" : "y",
        },
        carouselPlugins
        // [...(plugins || []), AutoScroll({ speed: 2, stopOnInteraction: false })]
    );
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const onSelect = useCallback((api: CarouselApi) => {
        if (!api) {
            return;
        }

        setCanScrollPrev(api.canScrollPrev());
        setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = useCallback(() => {
        api?.scrollPrev();
    }, [api]);

    const scrollNext = useCallback(() => {
        api?.scrollNext();
    }, [api]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === "ArrowLeft") {
                event.preventDefault();
                scrollPrev();
            } else if (event.key === "ArrowRight") {
                event.preventDefault();
                scrollNext();
            }
        },
        [scrollPrev, scrollNext]
    );

    useEffect(() => {
        if (!api || !setApi) {
            return;
        }

        setApi(api);
    }, [api, setApi]);

    useEffect(() => {
        if (!api) {
            return;
        }

        onSelect(api);
        api.on("reInit", onSelect);
        api.on("select", onSelect);

        return () => {
            api.off("reInit", onSelect);
            api.off("select", onSelect);
        };
    }, [api, onSelect]);

    return (
        <CarouselContext.Provider
            value={{
                carouselRef,
                api,
                opts,
                orientation: 
                    orientation ||
                    (opts?.axis === "y" ? "vertical" : "horizontal"),
                scrollPrev,
                scrollNext,
                canScrollPrev,
                canScrollNext,
            }}
        >
            <div
                onKeyDownCapture={handleKeyDown}
                className={cn("relative", className)}
                role="region"
                aria-roledescription="carousel"
                {...props}
            >
                {children}
            </div>
        </CarouselContext.Provider>
    );
};
Carousel.displayName = "Carousel";

const CarouselContent = ({
    className,
    ...props
}: HTMLAttributes<HTMLDivElement>) => {
    const { carouselRef, orientation } = useCarousel();

    return (
        <div ref={carouselRef} className="overflow-hidden">
            <div
                className={cn(
                    "flex",
                    orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
                    className
                )}
                {...props}
            />
        </div>
    );
};
CarouselContent.displayName = "CarouselContent";

const CarouselContentVisible = ({
    className,
    ...props
}: HTMLAttributes<HTMLDivElement>) => {
    const { carouselRef, orientation } = useCarousel();

    return (
        <div ref={carouselRef} className="w-full">
            <div
                className={cn(
                    "flex",
                    orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
                    className
                )}
                {...props}
            />
        </div>
    );
};
CarouselContentVisible.displayName = "CarouselContentVisible";

const CarouselItem = ({
    className,
    ...props
}: HTMLAttributes<HTMLDivElement>) => {
    const { orientation } = useCarousel();

    return (
        <div
            role="group"
            aria-roledescription="slide"
            className={cn(
                "min-w-0 shrink-0 grow-0 basis-full",
                orientation === "horizontal" ? "pl-4" : "pt-4",
                className
            )}
            {...props}
        />
    );
};

CarouselItem.displayName = "CarouselItem";

const CarouselPrevious = ({
    className,
    variant = "outline",
    size = "icon",
    children,
    ...props
}: ComponentProps<typeof Button>) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();

    return (
        <Button
            variant={variant}
            size={size}
            className={cn(
                "absolute h-8 w-8 bg-transparent border-none",
                orientation === "horizontal"
                    ? "-left-12 top-1/2 -translate-y-1/2"
                    : "-top-12 left-1/2 -translate-x-1/2 rotate-90",
                className
            )}
            disabled={!canScrollPrev}
            onClick={scrollPrev}
            {...props}
        >
            {children ?? <ArrowRightIcon className="h-4 w-4 rotate-180" />}
            <span className="sr-only">Previous slide</span>
        </Button>
    );
};

CarouselPrevious.displayName = "CarouselPrevious";

const CarouselNext = ({
    className,
    variant = "outline",
    size = "icon",
    children,
    ...props
}: ComponentProps<typeof Button>) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel();

    return (
        <Button
            variant={variant}
            size={size}
            className={cn(
                "absolute h-8 w-8 bg-transparent border-none",
                orientation === "horizontal"
                    ? "-right-12 top-1/2 -translate-y-1/2"
                    : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
                className
            )}
            disabled={!canScrollNext}
            onClick={scrollNext}
            {...props}
        >
            {children ?? <ArrowRightIcon className="h-4 w-4" />}
            <span className="sr-only">Next slide</span>
        </Button>
    );
};
CarouselNext.displayName = "CarouselNext";

export {
    type CarouselApi,
    Carousel,
    CarouselContent,
    CarouselContentVisible,
    CarouselItem,
    CarouselPrevious,
    CarouselNext,
};
